import React from "react"
import { modelRequest } from '@stem-sims/nexus';
import Modal from 'react-bootstrap/Modal'
import { toast } from 'react-toastify';
import { useRoster } from '.././StudentsContext';
import { Field, Formik, Form as FormikForm } from 'formik';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { PulseLoader } from 'react-spinners';
import { ModalType } from "../Import";


interface DefaultImportFormProps {
    setModalType: (modalType: ModalType) => void
}

export default function DefaultImportForm({ setModalType }: DefaultImportFormProps) {
    const roster = useRoster()

    return <>
        <Formik
            initialValues={{
                studentImportCsv: null,
                program: "reading",
            }}
            onSubmit={async ({ studentImportCsv, program }: { studentImportCsv: File, program: string }) => {
                if (!studentImportCsv) {
                    toast.error("No file chosen.")
                    return
                }

                const formData = new FormData()
                formData.append("program", program)
                formData.append("studentImportCsv", new Blob([studentImportCsv], { type: "text/csv" }))
                try {
                    await modelRequest.post("/api/students/import", formData)
                    toast.success("Imported Students!")
                    roster.refreshStudents()
                    roster.hideModals()
                } catch (e) {
                    toast.error(e)
                }
            }}
        >
            {({ isSubmitting, setFieldValue }) => (
                <FormikForm>
                    <Modal.Body className="g-3">
                        <Row>
                            <Col sm={3} className="text-sm-end my-auto">
                                <label htmlFor="studentImportCsv">Student CSV</label>
                            </Col>
                            <Col sm={8}>
                                <input type="file" name="studentImportCsv" accept=".csv" onChange={(event) => {
                                    const newFile = event.currentTarget.files[0]
                                    if (!newFile.name.toLowerCase().endsWith(".csv")) {
                                        toast.error("You must select a CSV file to upload.")
                                        event.currentTarget.value = null
                                        return
                                    }

                                    setFieldValue("studentImportCsv", newFile)
                                }} />
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col sm={3} className="text-sm-end my-auto">
                                <label htmlFor="program">License Type</label>
                            </Col>
                            <Col sm={8}>
                                <Field as="select" id="program" name="program" className="form-control">
                                    <option value="reading">Reading</option>
                                    <option value="math">Math</option>
                                    <option value="emergentReader">Emergent Reader</option>
                                </Field>
                            </Col>
                        </Row>

                        <Button variant="secondary" type="submit" size="lg" disabled={isSubmitting} className="d-block m-auto mt-2">
                            {isSubmitting ?
                                <PulseLoader />
                                :
                                <span>Import</span>
                            }
                        </Button>
                        <hr />
                        <Row>
                            <p>To bulk add students to your account, upload a CSV containing at least the required name and birthdate columns. All fields must be formatted as shown for the import to be successful.</p>
                            <p><b>Excel sheets must be saved as a .csv file in order to import students successfully.</b></p>
                            <table className="mx-3">
                                <thead>
                                    <tr>
                                        <th>Column Name</th>
                                        <th>Required</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td><b>Student Name</b></td>
                                        <td><b>Yes</b></td>
                                        <td><b>Name of the student in the system (first + last name/initial)</b></td>
                                    </tr>

                                    <tr>
                                        <td><b>Date Of Birth</b></td>
                                        <td><b>Yes</b></td>
                                        <td><b>Day of birth of the student (mm/dd/yyyy format)</b></td>
                                    </tr>

                                    <tr>
                                        <td>Ethnicity</td>
                                        <td>No</td>
                                        <td>
                                            <OverlayTrigger overlay={
                                                <Tooltip id="tooltip-ethnicity">
                                                    White, Hispanic Origin, Mideastern/Arab Descent, Black, Asican/Pacific Islander, South Asian, American Indian/Alaskan Native, Multi-Racial, Decline to Answer
                                                </Tooltip>
                                            }>
                                                <span>Student's ethnicity (hover for available options) <Button size="sm" variant="secondary" onClick={() => setModalType("ethnicity")}>Click for list</Button></span>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>Enrollment</td>
                                        <td>No</td>
                                        <td>
                                            <OverlayTrigger overlay={
                                                <Tooltip id="tooltip-enrollment">
                                                    Public, Private, Cyber, Homeschooling, Not Applicable, Decline to Answer
                                                </Tooltip>
                                            }>
                                                <span>Current school enrollment type (hover for available options) <Button size="sm" variant="secondary" onClick={() => setModalType("enrollment")}>Click for list</Button></span>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>IEP</td>
                                        <td>No</td>
                                        <td>
                                            <OverlayTrigger overlay={
                                                <Tooltip id="tooltip-iep">
                                                    Yes, No, Decline to Answer
                                                </Tooltip>
                                            }>
                                                <span>Whether student is on an IEP (hover for available options) <Button size="sm" variant="secondary" onClick={() => setModalType("iep")}>Click for list</Button></span>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>ELL</td>
                                        <td>No</td>
                                        <td>
                                            <OverlayTrigger overlay={
                                                <Tooltip id="tooltip-ell">
                                                    Yes, No, Decline to Answer
                                                </Tooltip>
                                            }>
                                                <span>Whether student is an ELL (hover for available options) <Button size="sm" variant="secondary" onClick={() => setModalType("ell")}>Click for list</Button></span>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <p>All optional fields must be provided exactly as spelled in the list, with no case sensitivity.</p>
                        </Row>

                        <Row>
                            <Col>
                                <Button variant="secondary" onClick={() => {
                                    window.location.href = process.env.PUBLIC_URL + "/static/files/Great Leaps Example Student Import.xlsx"
                                }}>Download Example Spreadsheet</Button>
                            </Col>
                        </Row>

                    </Modal.Body>
                </FormikForm>
            )}
        </Formik>
    </>
}
